import { FC, useRef, useState } from 'react';
import { Form, Formik, FormikProps, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { IndiceDto } from 'api/indice';
import { indicesAPI } from 'services/swagger-api';
import { useDialog } from 'hooks/useDialog';
import { FormikInputText } from 'components/forms/formik/FormikInputText';
import { CheckboxComponent } from 'components/forms/Checkbox';
import { FormField } from 'components/forms/FormField';
import { ModalShell } from 'back-office/shared/components/ModalShell';
import { Branch } from 'back-office/features/branches-view/useBranches';

interface IndiceFormValues {
  code: string;
  label: string;
  branchIds: string[];
}

const validationSchema = Yup.object().shape({
  code: Yup.string().trim().required('Le code est requis'),
  label: Yup.string().trim().required('Le libellé est requis'),
});

interface IndiceModalProps {
  /** Absent en création. */
  indice?: IndiceDto;
  branches: Branch[];
  onSuccess: () => void;
}

/**
 * Création et modification d'un indice.
 *
 * En modification, l'identité et les branches passent par deux routes distinctes : le
 * back sépare `PUT /indices/{id}` de `PUT /indices/{id}/branches`. L'écran n'en montre
 * qu'un seul formulaire et enchaîne les deux appels.
 */
export const IndiceModal: FC<IndiceModalProps> = ({ indice, branches, onSuccess }) => {
  const formikRef = useRef<FormikProps<IndiceFormValues>>(null);
  const { closeDialog } = useDialog();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditMode = Boolean(indice);

  const initialValues: IndiceFormValues = {
    code: indice?.code ?? '',
    label: indice?.label ?? '',
    branchIds: (indice?.branches ?? [])
      .map((branch) => branch.branchId)
      .filter(Boolean) as string[],
  };

  const handleSubmit = async (values: IndiceFormValues) => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      if (isEditMode) {
        const updated = await indicesAPI.updateIndice({
          indiceId: indice?.id as string,
          updateIndiceCommand: { code: values.code.trim(), label: values.label.trim() },
        });
        if (!updated.isSuccess) {
          toast.error(updated.errorMessage || 'La modification a échoué');
          return;
        }

        // Le rattachement remplace la liste entière : on transmet donc l'état voulu au
        // complet, pas seulement les branches ajoutées.
        const attached = await indicesAPI.setIndiceBranches({
          indiceId: indice?.id as string,
          rattacherBranchesIndiceCommand: { branchIds: values.branchIds },
        });
        if (!attached.isSuccess) {
          toast.error(attached.errorMessage || 'Le rattachement des branches a échoué');
          return;
        }
        toast.success('Indice modifié');
      } else {
        const created = await indicesAPI.createIndice({
          createIndiceCommand: {
            code: values.code.trim(),
            label: values.label.trim(),
            branchIds: values.branchIds,
          },
        });
        if (!created.isSuccess) {
          toast.error(created.errorMessage || 'La création a échoué');
          return;
        }
        toast.success('Indice créé');
      }

      onSuccess();
      closeDialog();
    } catch (error) {
      console.error("Enregistrement de l'indice impossible", error);
      toast.error("L'enregistrement a échoué");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      <ModalShell
        title={isEditMode ? indice?.label || "Modifier l'indice" : 'Créer un indice'}
        subtitle="Référentiel des indices"
        submit={{
          label: 'Sauvegarder',
          onClick: () => formikRef.current?.submitForm(),
          disabled: isSubmitting,
        }}
      >
        <Form className="flex flex-col gap-5 rounded-xl border border-gray-200 bg-white p-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormikInputText name="code" label="Code" required placeholder="ex. FFB" />
            <FormikInputText
              name="label"
              label="Libellé"
              required
              placeholder="ex. Indice FFB"
            />
          </div>

          <div className="border-t border-gray-100 pt-4">
            <BranchesField branches={branches} />
          </div>
        </Form>
      </ModalShell>
    </Formik>
  );
};

/**
 * Branches auxquelles l'indice s'applique.
 *
 * Un indice peut ne s'appliquer à aucune branche — il reste alors dans le référentiel
 * sans être proposé nulle part, ce que le filtre « sans branche » de la liste permet de
 * retrouver.
 */
const BranchesField: FC<{ branches: Branch[] }> = ({ branches }) => {
  const { values, setFieldValue } = useFormikContext<IndiceFormValues>();

  const toggle = (branchId: string, checked: boolean) =>
    setFieldValue(
      'branchIds',
      checked
        ? [...values.branchIds, branchId]
        : values.branchIds.filter((id) => id !== branchId)
    );

  return (
    <FormField label="Branches concernées" name="branchIds">
      <div className="grid max-h-64 grid-cols-1 gap-2 overflow-y-auto sm:grid-cols-2">
        {branches.map((branch) => (
          <CheckboxComponent
            key={branch.id}
            name={`branch-${branch.id}`}
            label={branch.label}
            checked={values.branchIds.includes(branch.id)}
            onChange={(checked) => toggle(branch.id, checked)}
          />
        ))}
      </div>
      <p className="mt-2 text-xs text-gray-400">
        {values.branchIds.length === 0
          ? "Aucune branche : l'indice restera dans le référentiel sans être proposé."
          : `${values.branchIds.length} branche${values.branchIds.length > 1 ? 's' : ''} sélectionnée${values.branchIds.length > 1 ? 's' : ''}.`}
      </p>
    </FormField>
  );
};
